import React from 'react';

import { Skeleton } from '@core-ui/Components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@core-ui/Components/ui/table';

interface SessionsListSkeletonProps {
  rows?: number;
}

/** Mirrors the SessionsList rows so the layout doesn't shift on first paint. */
export const SessionsListSkeleton: React.FC<SessionsListSkeletonProps> = ({ rows = 4 }) => (
  <div className="space-y-2">
    {Array.from({ length: rows }).map((_, index) => (
      <div
        key={index}
        className="w-full flex items-start gap-2.5 px-3 py-2.5 rounded-lg border border-border bg-card"
      >
        <Skeleton className="h-5 w-5 rounded-md mt-0.5" />
        <div className="flex-1 min-w-0 space-y-2">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

/** Skeleton row of chip-shaped placeholders for StatusFilterChips. */
export const StatusFilterChipsSkeleton: React.FC = () => (
  <div className="flex flex-wrap items-center gap-2">
    <Skeleton className="h-4 w-14 mr-1" />
    {Array.from({ length: 5 }).map((_, index) => (
      <Skeleton key={index} className="h-8 w-28 rounded-full" />
    ))}
  </div>
);

interface PreviewContactsTableSkeletonProps {
  rows?: number;
}

/** Mirrors PreviewContactsTable so the user sees the structure while data loads. */
export const PreviewContactsTableSkeleton: React.FC<PreviewContactsTableSkeletonProps> = ({ rows = 5 }) => (
  <div className="rounded-xl border border-border overflow-hidden">
    <Table>
      <TableHeader className="bg-muted/60">
        <TableRow className="border-b border-border/60 hover:bg-transparent">
          <TableHead className="font-semibold">
            <Skeleton className="h-3 w-14" />
          </TableHead>
          <TableHead className="font-semibold">
            <Skeleton className="h-3 w-14" />
          </TableHead>
          <TableHead className="font-semibold">
            <Skeleton className="h-3 w-16" />
          </TableHead>
          <TableHead className="font-semibold">
            <Skeleton className="h-3 w-16" />
          </TableHead>
          <TableHead className="font-semibold">
            <Skeleton className="h-3 w-14" />
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: rows }).map((_, index) => (
          <TableRow key={index} className="border-b border-border/40 hover:bg-transparent">
            <TableCell>
              <span className="inline-flex items-center gap-1.5">
                <Skeleton className="h-1.5 w-1.5 rounded-full" />
                <Skeleton className="h-3 w-14" />
              </span>
            </TableCell>
            <TableCell><Skeleton className="h-3 w-24" /></TableCell>
            <TableCell><Skeleton className="h-3 w-20" /></TableCell>
            <TableCell><Skeleton className="h-3 w-28" /></TableCell>
            <TableCell><Skeleton className="h-3 w-24" /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);
